import { gql } from "@apollo/client";
import { COUNTRIES, HOSTELS, type Poi } from "./content";
import type { RouteStop } from "./plannerLogic";

/**
 * "Get this trip": the route as `createTripFromRoute` takes it (the backend's input types: RouteStopInput,
 * RouteStayInput, RoutePlaceInput, CreateTripFromRouteInput). The mutation sits here, and not in graphql/mutations/trip.ts,
 * so the marketing bundle does not pull the whole trip fragment in for one call.
 */
export const CREATE_TRIP_FROM_ROUTE = gql`
	mutation CreateTripFromRoute($input: CreateTripFromRouteInput!) {
		createTripFromRoute(input: $input) {
			_id
			name
		}
	}
`;

/** the attribution tag: the backend reports it on its partner_trip_created event and does not store it on the trip */
export const PARTNER = "madmonkey";

/** what a trip built in the planner is called; a ready-made route keeps its own name */
export const BUILT_ROUTE_NAME = "My Mad Monkey route";

/** a place on the hostel's map, saved onto the stop; the backend leaves out anything it cannot use */
export interface RoutePlaceInput {
	name: string;
	latitude: number;
	longitude: number;
	/** the place's Google place id (`gid` in madMonkeyData.json): what the app recognises the place by */
	googlePlaceId: string;
	/** the knowledge base's category: attraction, restaurant, cafe, bar or experience */
	category: string;
	/** the map's one line about the place */
	description: string;
	/** on media.stippl.io; most places have none */
	photo?: string;
}

export interface RouteStopInput {
	city: string;
	country: string;
	latitude: number;
	longitude: number;
	nights: number;
	stay: {
		name: string;
		latitude: number;
		longitude: number;
		nuiteeHotelId: string;
		url: string;
	};
	/** the places the hostel's map shows, in the map's order; empty for a hostel without */
	places: RoutePlaceInput[];
}

export interface CreateTripFromRouteInput {
	name: string;
	stops: RouteStopInput[];
	partner: typeof PARTNER;
}

const placeInput = (p: Poi): RoutePlaceInput => ({
	name: p.name,
	latitude: p.lat,
	longitude: p.lon,
	googlePlaceId: p.gid,
	category: p.cat,
	description: p.why,
	// HOSTELS has already made it a full media.stippl.io URL; no key at all when there is no photo
	...(p.photo ? { photo: p.photo } : {}),
});

export function buildCreateTripInput(route: RouteStop[], name: string): CreateTripFromRouteInput {
	return {
		name,
		partner: PARTNER,
		stops: route.map(({ s, n }) => {
			const h = HOSTELS[s];
			return {
				city: h.name,
				country: COUNTRIES[h.cc],
				latitude: h.lat,
				longitude: h.lon,
				nights: n,
				stay: { name: `Mad Monkey ${h.name}`, latitude: h.lat, longitude: h.lon, nuiteeHotelId: h.nuitee, url: h.url },
				places: (h.pois ?? []).map(placeInput),
			};
		}),
	};
}
